import React, { createContext, useCallback, useContext, useState, useRef, useEffect } from 'react';
import { AlertTriangle, Trash2, HelpCircle } from 'lucide-react';

const ConfirmContext = createContext(null);

/**
 * ConfirmProvider — global styled confirm dialog replacing window.confirm().
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ message: 'Supprimer cet élément ?', variant: 'danger' });
 *   if (!ok) return;
 *
 * Options:
 *   title        — dialog title (default: 'Confirmer')
 *   message      — body text
 *   confirmLabel — confirm button label (default: 'Confirmer')
 *   cancelLabel  — cancel button label (default: 'Annuler')
 *   variant      — 'danger' | 'warning' | 'default'  (default: 'default')
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState({
    open: false, title: '', message: '',
    confirmLabel: 'Confirmer', cancelLabel: 'Annuler',
    variant: 'default', resolve: null,
  });

  const confirm = useCallback((opts = {}) => new Promise((res) => {
    setState({
      open: true,
      title: opts.title || 'Confirmer',
      message: opts.message || '',
      confirmLabel: opts.confirmLabel || 'Confirmer',
      cancelLabel: opts.cancelLabel || 'Annuler',
      variant: opts.variant || 'default',
      resolve: res,
    });
  }), []);

  const onClose = useCallback(() => {
    setState((s) => { s.resolve?.(false); return { ...s, open: false }; });
  }, []);

  const onConfirm = useCallback(() => {
    setState((s) => { s.resolve?.(true); return { ...s, open: false }; });
  }, []);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {state.open && <ConfirmModal {...state} onCancel={onClose} onConfirm={onConfirm} />}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx.confirm;
}

const VARIANT_STYLES = {
  danger: {
    icon: <Trash2 className="w-5 h-5 text-red-500" />,
    iconBg: 'bg-red-50',
    btn: 'bg-red-500 hover:bg-red-600 text-white focus:ring-red-300',
  },
  warning: {
    icon: <AlertTriangle className="w-5 h-5 text-amber-500" />,
    iconBg: 'bg-amber-50',
    btn: 'bg-amber-500 hover:bg-amber-600 text-white focus:ring-amber-300',
  },
  default: {
    icon: <HelpCircle className="w-5 h-5 text-accent" />,
    iconBg: 'bg-orange-50',
    btn: 'bg-accent hover:opacity-90 text-white focus:ring-accent/40',
  },
};

function ConfirmModal({ title, message, confirmLabel, cancelLabel, variant, onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.default;

  // Focus cancel button by default (safer UX), close on Escape
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative w-full max-w-sm rounded-2xl bg-white shadow-xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${styles.iconBg}`}>
            {styles.icon}
          </div>
          <h2 className="text-base font-bold text-gray-900">{title}</h2>
        </div>

        {/* Body */}
        {message && (
          <p className="px-5 pb-4 text-sm text-gray-500 leading-relaxed">{message}</p>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50/50">
          <button
            ref={cancelRef}
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 transition"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-semibold rounded-xl focus:outline-none focus:ring-2 transition ${styles.btn}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
